import type { QueryClient, QueryKey } from '@tanstack/react-query';

/**
 * Ties a route loader's fetch to the router's navigation: when the loader
 * is abandoned (the reader navigates away -- Back, a link, a new URL --
 * before it resolved), TanStack Router aborts its `abortController`, and
 * this cancels the query the loader started. `cancelQueries` rejects the
 * loader's pending `ensureQueryData`, aborts the request itself (the
 * `queryFn` forwards its `signal` to `graphqlFetch`), and reverts the
 * query to its previous state, so nothing half-loaded is kept or persisted.
 *
 * Needed on top of TanStack Query's own cancel-on-unmount because a
 * loader's `ensureQueryData` has no observer: while the loading skeleton is
 * up, no component is subscribed to the query yet, so there's no "last
 * observer unmounting" for Query to react to.
 *
 * Harmless once the fetch has settled: cancelling a query that isn't
 * fetching is a no-op.
 */
export function cancelQueryOnAbort(
	queryClient: QueryClient,
	queryKey: QueryKey,
	signal: AbortSignal,
): void {
	const cancel = () => {
		void queryClient.cancelQueries({ queryKey, exact: true });
	};
	if (signal.aborted) {
		cancel();
		return;
	}
	signal.addEventListener('abort', cancel, { once: true });
}
